import type { EntityType } from "@dantria/api/src/deletions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, unwrap } from "./api";

export type { EntityType };

/**
 * Delete a record, from wherever it happens to be listed.
 *
 * One hook rather than a mutation per page: the call is the same shape for
 * every kind of record, and the thing that varies — what else on screen is now
 * wrong — is the same answer every time. A deletion reverses ledger entries, so
 * it can move any figure in the app; the cache is dropped wholesale rather than
 * asking each caller to remember which of seven queries its delete invalidated.
 */
export function useDeleteRecord(slug: string) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (target: { type: EntityType; id: string }) =>
      unwrap(
        await api.api.c[":slug"].trash[":type"][":id"].$delete({
          param: { slug, type: target.type, id: target.id },
        }),
      ),
    onMutate: () => setError(null),
    onSuccess: () => queryClient.invalidateQueries(),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Couldn't delete that. Try again."),
  });

  return {
    remove: (type: EntityType, id: string) => mutation.mutate({ type, id }),
    deleting: mutation.isPending,
    error,
    clearError: () => setError(null),
  };
}
